import {
	BarChartOutlined,
	CommentOutlined,
	PushpinOutlined,
	SettingOutlined,
	TeamOutlined,
} from "@ant-design/icons";
import { ErrorComponent, useNotificationProvider } from "@refinedev/antd";
import { Authenticated, Refine } from "@refinedev/core";
import routerProvider, {
	DocumentTitleHandler,
	NavigateToResource,
	UnsavedChangesNotifier,
} from "@refinedev/react-router";
import { App as AntdApp, ConfigProvider } from "antd";
import jaJP from "antd/locale/ja_JP";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router";
import "@refinedev/antd/dist/reset.css";
import "./admin.css";

import { AdminLayout } from "./components/AdminLayout";
import { PlaceholderPage } from "./components/PlaceholderPage";
import { AuthCallbackPage } from "./pages/auth-callback";
import { ForbiddenPage } from "./pages/forbidden";
import { LoginPage } from "./pages/login";
import { ResetPasswordPage } from "./pages/reset-password";
import { StaffInvitePage } from "./pages/staff/invite";
import { StaffListPage } from "./pages/staff/list";
import { UsersListPage } from "./pages/users/list";
import { accessControlProvider } from "./providers/accessControlProvider";
import { authProvider } from "./providers/authProvider";
import { dataProvider } from "./providers/dataProvider";
import { tamsAntdTheme } from "./theme";

function AdminRefine() {
	const notificationProvider = useNotificationProvider();

	return (
		<Refine
			routerProvider={routerProvider}
			dataProvider={dataProvider}
			authProvider={authProvider}
			accessControlProvider={accessControlProvider}
			notificationProvider={notificationProvider}
			resources={[
				{
					name: "users",
					list: "/users",
					meta: {
						label: "ユーザー管理",
						icon: <TeamOutlined />,
					},
				},
				{
					name: "posts",
					list: "/posts",
					meta: {
						label: "投稿コメント管理",
						icon: <CommentOutlined />,
					},
				},
				{
					name: "pins",
					list: "/pins",
					meta: {
						label: "ピン留め管理",
						icon: <PushpinOutlined />,
					},
				},
				{
					name: "reports",
					list: "/reports",
					meta: {
						label: "統計・レポート",
						icon: <BarChartOutlined />,
					},
				},
				{
					name: "staff",
					list: "/staff",
					create: "/staff/invite",
					meta: {
						label: "スタッフ管理",
						icon: <SettingOutlined />,
					},
				},
			]}
			options={{
				syncWithLocation: true,
				warnWhenUnsavedChanges: true,
				title: {
					text: "たむたむ広場 管理画面",
				},
			}}
		>
			<Routes>
				<Route
					element={
						<Authenticated key="authenticated-outer" redirectOnFail="/login">
							<AdminLayout>
								<Outlet />
							</AdminLayout>
						</Authenticated>
					}
				>
					<Route index element={<Navigate to="/users" replace />} />
					<Route path="/users" element={<UsersListPage />} />
					<Route
						path="/posts"
						element={
							<PlaceholderPage title="投稿コメント管理" subtitle="準備中" />
						}
					/>
					<Route
						path="/pins"
						element={<PlaceholderPage title="ピン留め管理" subtitle="準備中" />}
					/>
					<Route
						path="/reports"
						element={
							<PlaceholderPage title="統計・レポート" subtitle="準備中" />
						}
					/>
					<Route path="/staff" element={<StaffListPage />} />
					<Route path="/staff/invite" element={<StaffInvitePage />} />
				</Route>

				<Route
					element={
						<Authenticated key="authenticated-inner" fallback={<Outlet />}>
							<NavigateToResource resource="users" />
						</Authenticated>
					}
				>
					<Route path="/login" element={<LoginPage />} />
					<Route path="/sign-in" element={<LoginPage />} />
				</Route>

				<Route path="/auth/callback" element={<AuthCallbackPage />} />
				<Route path="/reset-password" element={<ResetPasswordPage />} />
				<Route path="/forbidden" element={<ForbiddenPage />} />
				<Route path="*" element={<ErrorComponent />} />
			</Routes>
			<UnsavedChangesNotifier />
			<DocumentTitleHandler
				handler={({ resource, pathname, autoGeneratedTitle }) => {
					if (pathname?.startsWith("/users") || resource?.name === "users") {
						return "ユーザー管理 | たむたむ広場";
					}
					if (pathname?.startsWith("/posts")) {
						return "投稿コメント管理 | たむたむ広場";
					}
					if (pathname?.startsWith("/pins")) {
						return "ピン留め管理 | たむたむ広場";
					}
					if (pathname?.startsWith("/reports")) {
						return "統計・レポート | たむたむ広場";
					}
					if (pathname === "/staff/invite") {
						return "スタッフ招待 | たむたむ広場";
					}
					if (pathname?.startsWith("/staff") || resource?.name === "staff") {
						return "スタッフ管理 | たむたむ広場";
					}
					if (pathname === "/login" || pathname === "/sign-in") {
						return "サインイン | たむたむ広場";
					}
					return autoGeneratedTitle
						? `${autoGeneratedTitle} | たむたむ広場`
						: "たむたむ広場 管理画面";
				}}
			/>
		</Refine>
	);
}

export function App() {
	return (
		<BrowserRouter>
			<ConfigProvider locale={jaJP} theme={tamsAntdTheme}>
				<AntdApp>
					<AdminRefine />
				</AntdApp>
			</ConfigProvider>
		</BrowserRouter>
	);
}
